import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  X,
  Package,
  Search,
  Plus,
  Minus,
  Trash2,
  User,
  Loader2,
  Tag,
  AlertCircle,
  ShoppingCart,
} from "lucide-react";
import { showError, showSuccess } from "../../../utils/toastUtils";
import RejectRequestModal from "./RejectRequestModal";

const AssignProductsModal = ({
  isOpen,
  booking,
  selectedService,
  onClose,
  onSuccess,
}) => {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [requestedProducts, setRequestedProducts] = useState([]);
  const [assignmentId, setAssignmentId] = useState(null); // ✅ ADD THIS STATE
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequestForRejection, setSelectedRequestForRejection] =
    useState(null);
  const [isRejectingRequest, setIsRejectingRequest] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchProducts();
      fetchExistingAssignments();
    } else {
      // Reset on close
      setSelectedProducts([]);
      setSearchTerm("");
      setRequestedProducts([]);
      setAssignmentId(null); // ✅ RESET ON CLOSE
    }
  }, [isOpen, selectedService]);

  const fetchProducts = async () => {
    setIsFetching(true);
    try {
      const productsRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/products/get-all`
      );

      setProducts(productsRes.data.data || []);
    } catch (error) {
      console.error("Failed to fetch products:", error);
      showError("Failed to load products");
    } finally {
      setIsFetching(false);
    }
  };

  const fetchExistingAssignments = async () => {
    if (!booking?._id) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/products-assignment/${booking._id}`
      );

      const assignment = response.data.data;

      // ✅ STORE THE ACTUAL ASSIGNMENT ID
      if (assignment?._id) {
        setAssignmentId(assignment._id);
      }

      // Pre-fill with existing assigned products for this service
      if (assignment?.products && selectedService) {
        const serviceCategory =
          selectedService.serviceId?.categoryId?.categoryName;

        const existingProducts = assignment.products
          .filter(
            (p) =>
              p.product?.service?.toLowerCase() ===
              serviceCategory?.toLowerCase()
          )
          .map((p) => ({
            ...p.product,
            assignedQuantity: p.assignedQuantity,
            maxQuantity: p.product.quantity + p.assignedQuantity,
            _id: p.product._id,
          }));

        setSelectedProducts(existingProducts);
      }

      // Set requested products
      setRequestedProducts(assignment?.requestedProducts || []);
    } catch (error) {
      console.log("No existing assignments");
      setRequestedProducts([]);
      setAssignmentId(null); // ✅ CLEAR IF NO ASSIGNMENT
    }
  };

  // Helper functions for requested products
  const isProductRequested = (productId) => {
    return requestedProducts.some(
      (req) => req.product?._id === productId && req.status === "pending"
    );
  };

  const getRequestedQuantity = (productId) => {
    const request = requestedProducts.find(
      (req) => req.product?._id === productId && req.status === "pending"
    );
    return request?.requestedQuantity || 0;
  };

  // Filter products by selected service category
  const serviceCategory = selectedService?.serviceId?.categoryId?.categoryName;

  const filteredProducts = products.filter((p) => {
    const matchesService = serviceCategory?.toLowerCase()
      ? p?.service?.toLowerCase() === serviceCategory.toLowerCase()
      : true;

    const matchesSearch =
      searchTerm.trim() === "" ||
      p.productName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.brand?.toLowerCase().includes(searchTerm.toLowerCase())

    return matchesService && matchesSearch;
  });

  const handleAddProduct = (product) => {
    const exists = selectedProducts.find((p) => p._id === product._id);
    if (exists) {
      showError("Product already added");
      return;
    }

    if (product.quantity < 1) {
      showError("Product out of stock");
      return;
    }

    setSelectedProducts([
      ...selectedProducts,
      {
        ...product,
        assignedQuantity: 1,
        maxQuantity: product.quantity,
      },
    ]);
  };

  const handleQuantityChange = (productId, change) => {
    const product = selectedProducts.find((p) => p._id === productId);
    if (!product) return;

    const newQty = product.assignedQuantity + change;

    if (newQty < 1) return;

    if (newQty > product.maxQuantity) {
      showError(`Only ${product.maxQuantity} items available`);
      return;
    }

    setSelectedProducts((prev) =>
      prev.map((p) =>
        p._id === productId ? { ...p, assignedQuantity: newQty } : p
      )
    );
  };

  const handleApproveRequest = async (request) => {
    // ✅ CHECK IF ASSIGNMENT ID EXISTS
    if (!assignmentId) {
      showError("Assignment not found");
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/products-assignment/review-product-request/${assignmentId}`,
        {
          productId: request.product._id,
          action: "approve",
        }
      );

      showSuccess(`${request.product.productName} approved and assigned`);
      fetchExistingAssignments(); // Refresh data
    } catch (error) {
      console.error("Approve error:", error);
      showError(
        error?.response?.data?.message || "Failed to approve request"
      );
    }
  };

  const handleRejectRequest = async (rejectionReason) => {
    if (!rejectionReason || !rejectionReason.trim()) {
      showError("Rejection reason is required");
      return;
    }

    if (!selectedRequestForRejection?.product?._id) {
      showError("Invalid product selection");
      return;
    }

    // ✅ CHECK IF ASSIGNMENT ID EXISTS
    if (!assignmentId) {
      showError("Assignment not found");
      return;
    }

    setIsRejectingRequest(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/products-assignment/review-product-request/${assignmentId}`,
        {
          productId: selectedRequestForRejection.product._id,
          action: "reject",
          rejectionReason: rejectionReason,
        }
      );

      showSuccess("Request rejected");
      fetchExistingAssignments();
      setShowRejectModal(false);
      setSelectedRequestForRejection(null);
    } catch (error) {
      console.error("Reject error:", error);
      showError(error?.response?.data?.message || "Failed to reject request");
    } finally {
      setIsRejectingRequest(false);
    }
  };

  const handleRemoveProduct = (productId) => {
    setSelectedProducts((prev) => prev.filter((p) => p._id !== productId));
  };

  const handleSubmit = async () => {
    if (selectedProducts.length === 0) {
      showError("Please select at least one product");
      return;
    }

    if (!booking?.staffId?._id) {
      showError("This booking must have an assigned staff member first");
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/products-assignment/assign`,
        {
          bookingId: booking._id,
          staffId: booking.staffId._id,
          products: selectedProducts.map((p) => ({
            productId: p._id,
            quantity: p.assignedQuantity,
          })),
        }
      );

      showSuccess(
        `${selectedProducts.length} product(s) assigned to booking successfully`
      );
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Failed to assign products:", error);
      showError(
        error?.response?.data?.message || "Failed to assign products"
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !booking) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b bg-gradient-to-r  from-accentTextColor/80 via-[#e8ab99] to-accentTextColor/80">
          <div className="flex items-center gap-3 text-white">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Package size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold">
                Assign Products to Service
              </h2>
              <p className="text-xs text-white">
                {selectedService?.serviceId?.name || "Service"}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition"
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        {isFetching ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-gray-400" size={40} />
          </div>
        ) : (
          <>
            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Service Information */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-sm font-bold text-primaryTextColor mb-3 flex items-center gap-2">
                  <Tag size={16} />
                  Service Details
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-600">Service Name</p>
                    <p className="font-medium">
                      {selectedService?.serviceId?.name || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Category</p>
                    <p className="font-medium">{serviceCategory || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Duration</p>
                    <p className="font-medium">
                      {selectedService?.serviceId?.duration || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Price</p>
                    <p className="font-medium">
                      ₹{selectedService?.price || 0}
                    </p>
                  </div>
                </div>
                {booking?.staffId?._id ? (
                  <div className="mt-3 bg-green-50 border border-green-300 rounded-lg p-3 text-sm">
                    <p className="text-gray-600">Assigned Staff</p>
                    <p className="font-medium text-green-800 flex items-center gap-1">
                      <User size={14} />
                      {booking.staffId.name}
                    </p>
                  </div>
                ) : (
                  <div className="mt-3 bg-yellow-50 border border-yellow-300 rounded-lg p-3 text-sm text-yellow-800">
                    ⚠️ Please assign a staff member to this booking first
                    before assigning products.
                  </div>
                )}
              </div>

              {/* Staff Requested Products */}
              {requestedProducts.filter((rp) => rp.status === "pending")
                .length > 0 && (
                  <div className="bg-blue-50 border-2 border-blue-400 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-bold text-blue-800 flex items-center gap-2">
                        <ShoppingCart size={16} />
                        Staff Requested Products (
                        {
                          requestedProducts.filter((rp) => rp.status === "pending")
                            .length
                        }
                        )
                      </h3>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                        Pending Review
                      </span>
                    </div>

                    <div className="space-y-2 mb-3">
                      {requestedProducts
                        .filter((rp) => rp.status === "pending")
                        .map((request, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between bg-white rounded-lg p-3 border border-blue-200"
                          >
                            <div className="flex items-center gap-3 flex-1">
                              {request.product?.productImg ? (
                                <img
                                  src={request.product.productImg}
                                  alt={request.product.productName}
                                  className="w-12 h-12 rounded-lg object-cover"
                                />
                              ) : (
                                <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                                  <Package size={20} className="text-gray-400" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800 truncate">
                                  {request.product?.productName}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {request.product?.brand} • Stock:{" "}
                                  {request.product?.quantity}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <p className="text-xs text-blue-600">Requested</p>
                                <p className="text-sm font-bold text-blue-700">
                                  {request.requestedQuantity}
                                </p>
                              </div>

                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleApproveRequest(request)}
                                  className="px-3 py-1.5 bg-green-500 text-white text-xs font-medium rounded-lg hover:bg-green-600 transition active:scale-95"
                                >
                                  ✓ Approve
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedRequestForRejection(request);
                                    setShowRejectModal(true);
                                  }}
                                  className="px-3 py-1.5 bg-red-500 text-white text-xs font-medium rounded-lg hover:bg-red-600 transition active:scale-95"
                                >
                                  ✕ Reject
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>

                    <div className="flex items-center gap-2 text-xs text-blue-700 bg-blue-100 p-2 rounded-lg">
                      <AlertCircle size={14} />
                      <span>
                        These products are highlighted below with blue border.
                        Approve to auto-assign.
                      </span>
                    </div>
                  </div>
                )}

              {/* Product Search */}
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
                  <Search size={16} />
                  Search Products{" "}
                  {serviceCategory && `(${serviceCategory} category)`}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name, brand..."
                    className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accentTextColor"
                  />
                  <Search
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                </div>
              </div>

              {/* Available Products */}
              <div>
                <h3 className="text-sm font-bold text-primaryTextColor mb-3">
                  Available Products
                  {serviceCategory && (
                    <span className="text-xs font-normal text-gray-600 ml-2">
                      (Filtered by {serviceCategory})
                    </span>
                  )}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {filteredProducts.length === 0 ? (
                    <div className="col-span-2 text-center py-8 text-gray-500">
                      <Package
                        size={40}
                        className="mx-auto mb-2 text-gray-300"
                      />
                      <p className="text-sm">
                        No products found for this service category
                      </p>
                    </div>
                  ) : (
                    filteredProducts.map((product) => {
                      const isAlreadySelected = selectedProducts.find(
                        (p) => p._id === product._id
                      );
                      const isRequested = isProductRequested(product._id);
                      const requestedQty = getRequestedQuantity(product._id);

                      return (
                        <div
                          key={product._id}
                          className={`flex items-center gap-3 p-3 border rounded-lg transition ${isRequested
                            ? "border-blue-400 bg-blue-50 ring-2 ring-blue-200 shadow-sm"
                            : isAlreadySelected
                              ? "border-green-300 bg-green-50"
                              : "border-gray-200 hover:bg-gray-50"
                            }`}
                        >
                          {product.productImg ? (
                            <img
                              src={product.productImg}
                              alt={product.productName}
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                              <Package size={20} className="text-gray-400" />
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm truncate">
                                {product.productName}
                              </p>
                              {isRequested && (
                                <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full font-medium animate-pulse">
                                  Requested: {requestedQty}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">
                              {product.brand} • Stock: {product.quantity}
                            </p>
                          </div>

                          <button
                            onClick={() => handleAddProduct(product)}
                            disabled={product.quantity < 1 || isAlreadySelected}
                            className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                          >
                            {isAlreadySelected ? (
                              <span className="text-xs px-1">Added</span>
                            ) : (
                              <Plus size={16} />
                            )}
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Selected Products */}
              {selectedProducts.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-primaryTextColor mb-3">
                    Selected Products ({selectedProducts.length})
                  </h3>
                  <div className="space-y-3 border border-gray-200 rounded-lg p-3 bg-gray-50">
                    {selectedProducts.map((product) => (
                      <div
                        key={product._id}
                        className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg"
                      >
                        {product.productImg ? (
                          <img
                            src={product.productImg}
                            alt={product.productName}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                            <Package size={20} className="text-gray-400" />
                          </div>
                        )}

                        <div className="flex-1">
                          <p className="font-medium text-sm">
                            {product.productName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {product.brand} • Available: {product.maxQuantity}
                          </p>
                        </div>

                        {/* Quantity Controls */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() =>
                              handleQuantityChange(product._id, -1)
                            }
                            className="p-1.5 bg-gray-200 rounded-lg hover:bg-gray-300 transition"
                          >
                            <Minus size={14} />
                          </button>

                          <span className="w-10 text-center font-semibold text-sm">
                            {product.assignedQuantity}
                          </span>

                          <button
                            onClick={() => handleQuantityChange(product._id, 1)}
                            className="p-1.5 bg-gray-200 rounded-lg hover:bg-gray-300 transition"
                          >
                            <Plus size={14} />
                          </button>
                        </div>

                        <button
                          onClick={() => handleRemoveProduct(product._id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-5 border-t bg-gray-50 flex gap-3">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={
                  isLoading ||
                  !booking?.staffId?._id ||
                  selectedProducts.length === 0
                }
                className="flex-1 px-6 py-3 bg-primaryBtnColor text-white font-medium rounded-lg hover:bg-primaryBtnHoverColor transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Assigning...
                  </>
                ) : (
                  <>
                    <Package size={18} />
                    Assign Products
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>

      <RejectRequestModal
        isOpen={showRejectModal}
        product={selectedRequestForRejection?.product}
        onClose={() => {
          setShowRejectModal(false);
          setSelectedRequestForRejection(null);
        }}
        onConfirm={(reason) => {
          handleRejectRequest(reason);
        }}
        isRejecting={isRejectingRequest}
      />
    </div>
  );
};

export default AssignProductsModal;